import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import {
  ScheduleSlot,
  Specialist,
  useSpecialistScheduleContext,
  withSpecialistScheduleContext,
} from '@mindly/ui-components';

import { useSelector } from 'react-redux';
import { isLoadingSpecialistByIDSelector, specialistSelectorPositionById } from 'src/redux/specialists/selector';
import { useFavouriteSpecialist } from 'src/hooks/useFavouriteSpecialist';
import { PrivateRoutes } from 'src/routes/navigation';
import { TranslatedSpecialistCardWithScheduleWidget } from 'src/translated-components';
import { useAppointment } from 'src/hooks/useAppointment/useAppointment';
import { userSelector } from 'src/redux/user/selector';
import { ImpactStyle } from '@capacitor/haptics';
import { useHistory } from 'react-router';
import { useAnalytics } from 'src/shared';
import { useFilters } from 'src/shared/hooks/useFilters';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { useTranslation } from 'react-i18next';
import { ConfirmAppointmentFeatureRef } from 'src/features/ConfirmAppointmentFeature/types';
import ConfirmAppointmentFeature from 'src/features/ConfirmAppointmentFeature';
import { DateTime } from 'luxon';

type Props = {
  specialist: Specialist;
};

const PsychologistItem: React.FC<Props> = ({ specialist }) => {
  const { toggleFavourite } = useFavouriteSpecialist();
  const history = useHistory();
  const currentUser = useSelector(userSelector);
  const isBookingLoading = useSelector(isLoadingSpecialistByIDSelector);
  const specialistOrderPosition = useSelector(specialistSelectorPositionById(specialist.id));
  const { trackPredefinedEvent } = useAnalytics();
  const { filtersVersion } = useFilters();
  const { t } = useTranslation('ui');
  const confirmAppointmentRef = useRef<ConfirmAppointmentFeatureRef>(null);
  const { selectedSlot, setSelectedSlot, setSchedule } = useSpecialistScheduleContext();
  const specialistPosition = useSelector(specialistSelectorPositionById(specialist.id));

  const onCardClickHandler = useCallback(async () => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    trackPredefinedEvent('SPECIALIST_LIST_SPECIALIST_PROFILE_CLICKED', {
      specialist_id: specialist.id,
      specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
      specialist_order_position: specialistOrderPosition,
      specialist_list_algorithm_version: filtersVersion,
    });
    history.push(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace(':id', specialist.id));
  }, [filtersVersion, trackPredefinedEvent]);

  useLayoutEffect(() => {
    if (specialist.available_schedule) {
      setSchedule(specialist.available_schedule);
    }
  }, [specialist.available_schedule, setSchedule]);

  const { pushToAppointmentPage } = useAppointment();

  const handleToggleFavourite = () => {
    toggleFavourite(specialist);
  };

  const handleScheduleShowAll = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      trackPredefinedEvent('SPECIALIST_LIST_SHOW_ALL_SLOTS_CLICKED', specialistShortData);
      pushToAppointmentPage(specialist.id);
    },
    [pushToAppointmentPage, specialist.id]
  );

  const specialistShortData = useMemo(() => {
    return {
      specialist_id: specialist.id,
      specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
      specialist_order_position: specialistPosition,
    };
  }, [specialist, specialistPosition]);

  const handleSlotSelected = useCallback(
    (slot: ScheduleSlot) => {
      trackPredefinedEvent('SPECIALIST_LIST_SLOT_SELECTED', {
        ...specialistShortData,
        date_chosen: DateTime.fromISO(slot).toFormat('yyyy-MM-dd HH:mm'),
      });
      // need to wait for update selected slot
      setTimeout(() => {
        confirmAppointmentRef.current?.handleBookingClick();
      }, 0);
    },
    [trackPredefinedEvent, specialistShortData]
  );

  const events = useMemo(() => {
    return {
      onScheduleShowAll: handleScheduleShowAll,
      onScheduleSlotChange: handleSlotSelected,
    };
  }, [handleScheduleShowAll, handleSlotSelected]);

  return (
    <>
      <TranslatedSpecialistCardWithScheduleWidget
        specialist={specialist}
        onToggleFavourite={handleToggleFavourite}
        onCardClick={onCardClickHandler}
        currentUser={currentUser as any}
        isBookingLoading={isBookingLoading}
        t={t}
        events={events}
      />
      <ConfirmAppointmentFeature
        ref={confirmAppointmentRef}
        specialist={specialist as unknown as Specialist}
        selectedSlot={selectedSlot}
        onClose={() => setSelectedSlot(undefined)}
      />
    </>
  );
};

export default withSpecialistScheduleContext(PsychologistItem);
